import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';

import { TsSnackbarService } from 'tsui';
import { Store } from '@ngxs/store';
import { UserState } from '@state/user/state';
import { AccountState } from '@state/account/state';
import { FileFormat } from '@shared/enums';
import { FeatureFlag } from '@core/enums';
import { PermissionUtils } from '@core/utils/permission.utils';
import { generateDownloadableFile } from '@shared/functions';
import { DirectivesModule } from '@shared/directives/directives.module';
import { CustomField } from '@shared/modules/custom-form/custom-form.types';
import { CustomObjectsService } from '../../services/custom-objects.service';
import { CustomObject, CustomObjectValue } from '../../custom-objects.types';
import { GenerateMergeDocumentComponent } from '@shared/modals/generate-merge-document/generate-merge-document.component';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
@Component({
  selector: 'app-custom-objects-dashboard-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatMenuModule,
    MatIconModule,
    MatButtonModule,
    RouterLink,
    RouterLinkActive,
    DirectivesModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomObjectsDashboardHeaderComponent {
  @Input() set customObjectValue(value: CustomObjectValue) {
    this._customObjectValue.set(value);

    this.linkedCustomObjects.set(
      this.store.selectSnapshot(ObjectsManagerState.getLinkedObjects(value.customObject)).filter((x) => x.id),
    );

    this._coSearchField.set(
      this._customObjectValue().customObject.custom_fields.find(
        (field) => field.id === this._customObjectValue().customObject.search_custom_field_id,
      ),
    );

    this.setBreadcrumbTitle(this._customObjectValue());
  }

  @Output() changeLayout = new EventEmitter();
  @Output() duplicate = new EventEmitter();

  faceValue: WritableSignal<string> = signal<string>('');
  linkedCustomObjects: WritableSignal<CustomObject[]> = signal<CustomObject[]>([]);
  private _customObjectValue: WritableSignal<CustomObjectValue> = signal<CustomObjectValue>(null);
  private _coSearchField: WritableSignal<CustomField> = signal<CustomField>(null);

  get customObjectValue(): CustomObjectValue {
    return this._customObjectValue();
  }

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private _matDialog: MatDialog,
    private readonly customObjectsService: CustomObjectsService,
    private readonly snackBar: TsSnackbarService,
    private readonly store: Store,
  ) {}

  private setBreadcrumbTitle(value: CustomObjectValue): void {
    if (this._coSearchField()) {
      const fieldName = this._coSearchField().name;
      const rawValue = value[fieldName];

      // Convert to string if it's a number, otherwise use as is
      const stringValue = typeof rawValue === 'number' ? rawValue.toString() : rawValue;

      // Check if the value is a string and contains a pipe
      const headerValue =
        typeof stringValue === 'string' && stringValue?.includes('|') ? stringValue.split('|')[1] : stringValue;

      this.faceValue.set(headerValue);
    } else {
      this.faceValue.set(`#${this.customObjectValue.id}`);
    }
  }

  openGenerateModal(): void {
    const dialogRef = this._matDialog.open(GenerateMergeDocumentComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: {},
      width: '40vw',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.file_resource_id) {
          if (result.action && result.action === 'generate') {
            this.generateCustomOobjectValuePdf(result.format, result.file_resource_id, result.file_name);
          } else if (result.action && result.action === 'docusign') {
            this.generateDocusign(result.format, result.file_resource_id, result.file_name);
          }
        }
      });
  }

  generateCustomOobjectValuePdf(format: string = 'pdf', fileResourceId: number = null, fileName: string = null): void {
    this.snackBar.open('Download will begin shortly.');
    const account = this.store.selectSnapshot(AccountState.getAccount);

    this.customObjectsService
      .generateCustomObjectValueDocument(
        +account.id,
        this.customObjectValue.customObject?.id,
        this.customObjectValue.id,
        format,
        fileResourceId,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        if (response.data) {
          if ([FileFormat.PDF, FileFormat.DOCX].includes(format as FileFormat)) {
            generateDownloadableFile(response.data, format as FileFormat, `${this.customObjectValue.id} - ${fileName}`);
          }
        }
      });
  }

  generateDocusign(format: string = 'pdf', fileResourceId: number = null, fileName: string = null): void {
    this.snackBar.open('Download will begin shortly.');
    const account = this.store.selectSnapshot(AccountState.getAccount);

    this.customObjectsService
      .generateCustomObjectValueDocument(
        +account.id,
        this.customObjectValue.customObject?.id,
        this.customObjectValue.id,
        format,
        fileResourceId,
        true,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        if (response.data) {
          if (response.url) {
            window.open(response.url, '_blank');
          } else {
            if ([FileFormat.PDF, FileFormat.DOCX].includes(format as FileFormat)) {
              generateDownloadableFile(
                response.data,
                format as FileFormat,
                `${this.customObjectValue.id} - ${fileName}`,
              );
            }
          }
        }
      });
  }

  canEditLayout(customObjectName: string): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      const permission = PermissionUtils.generateCustomObjectPermissions(customObjectName);
      return this.store.selectSnapshot(UserState.hasPermission(permission.MANAGE_LAYOUT));
    }
    return true;
  }
}
